import cn from 'classnames';
import { useTransition, easings, animated } from 'react-spring';
import { useEffect, useState, memo } from 'react';
import { IModule } from '@api/types/general';
import { calcChanceToWin } from '@utils/jackpot';
import { IStakeObject, TJackpotTickets } from '@api/types/jackpot';
import { FIRST_TICKET } from '@consts/jackpot';
import Player from './player';
import PlayerDeposit from './player-deposit';
// import PlayerPlaceholder from "./player-placeholder";
import useReduxState from '@hooks/useReduxState';
import styles from './stats.module.scss';
import useJackpotRoom from '@hooks/useJackpotRoom';

const FROM_TICKETS_INITIAL_STATE: number[] = [FIRST_TICKET];

const JackpotStats = ({ className = '' }: IModule): JSX.Element | null => {
    const [{ rooms }] = useReduxState((state) => state.jackpot);
    const { currentRoom } = useJackpotRoom();
    const [fromTickets, setFromTickets] = useState<number[]>(
        FROM_TICKETS_INITIAL_STATE,
    );

    // eslint-disable-next-line no-unused-vars
    const [closeStats, setCloseStats] = useState(false);

    const game = rooms[currentRoom].current_game;

    useEffect(() => {
        let timer: any;
        if (game.game_metadata.status === 'finished') {
            timer = setTimeout(() => {
                setCloseStats(true);
                setTimeout(() => {
                    setCloseStats(false);
                }, 5000);
            }, 20000);
        }
        return () => clearTimeout(timer);
    }, [game.game_metadata.status]);

    const openingTrans = useTransition(game.players_joined > 1 && !closeStats, {
        config: {
            duration: 1000,
        },
        from: {
            maxHeight: '0px',
        },
        enter: {
            maxHeight: '900px',
        },
        leave: {
            maxHeight: '0px',
        },
    });

    /* Animations */

    const animationProps = {
        from: { opacity: 0, y: 100, scale: 0.5 },
        to: { opacity: 1, y: 0, scale: 1 },
        config: {
            duration: 1000,
            easing: easings.easeInOutElastic,
        },
    };

    // const placeholderPlayerSpring = useSpring({
    // from: animationProps.from,
    // to: animationProps.to,
    // config: animationProps.config,
    // });

    const mountPlayers = useTransition(game.players, {
        from: animationProps.from,
        enter: animationProps.to,
        keys: (player) => player.user.id,
        config: {
            duration: 300,
            easing: easings.easeOutBounce,
        },
    });

    const mountDeposits = useTransition([...game.stakes].reverse(), {
        from: { y: 50, opacity: 0 },
        enter: { y: 0, opacity: 1 },
        keys: (stake) => stake._id,
        trail: 200,
        config: animationProps.config,
    });

    useEffect(() => {
        !game.stakes.length && setFromTickets(FROM_TICKETS_INITIAL_STATE);
    }, [game.stakes]);

    const isPlayerLikelyToWin = (playerId: string): boolean => {
        if (game.players_joined === 0) return false;

        const chances = game.players.map(({ total_stake, user }) => ({
            id: user.id,
            chance: calcChanceToWin({ bank: game.bank, total_stake }),
        }));

        const playerLikelyToWin = chances.reduce(
            (maxChance, current) =>
                current.chance > maxChance.chance ? current : maxChance,
            { id: chances[0].id, chance: chances[0].chance },
        );

        return playerLikelyToWin.id === playerId;
    };

    const getPlayerTickets = (
        stake: number,
        toIndex: number,
    ): TJackpotTickets => ({
        from: fromTickets[toIndex],
        to: toIndex > 0 ? stake + fromTickets[toIndex] - FIRST_TICKET : stake,
    });

    const isTheWinner = (playerId?: string): boolean =>
        !game.winner_id || !playerId ? false : game.winner_id === playerId;

    const formatDepositData = (stake: IStakeObject, depositIndex: number) => ({
        stake,
        player: game.players.find((player) => player.user.id === stake.userId),
        tickets: getPlayerTickets(stake.stake, depositIndex),
        bank: game.bank,
        prevStake:
            depositIndex === 0
                ? 0
                : game.stakes.slice(0, depositIndex).reduce((prev, next) => {
                      return prev + next.stake;
                  }, 0),
    });

    /* Components */

    const PlayersList = mountPlayers((style, player, _, idx) => (
        <Player
            key={player.user.id || idx}
            playerNumber={idx + 1}
            data={player}
            isLikelyToWin={isPlayerLikelyToWin(player.user.id)}
            isWinner={isTheWinner(player.user.id)}
            {...{ style }}
        />
    ));

    const PlayersDeposits = mountDeposits((style, stake, _, idx) => {
        return (
            <PlayerDeposit
                key={stake._id || idx}
                playerNumber={idx}
                data={formatDepositData(stake, idx)}
                isLikelyToWin={isPlayerLikelyToWin(stake.userId)}
                isWinner={isTheWinner(stake.userId)}
                {...{ style, setFromTickets }}
            />
        );
    });

    // const PlayerPlaceholderItem = (
    //   <PlayerPlaceholder
    //     style={placeholderPlayerSpring}
    //     className={styles.placeholder}
    //   />
    // );

    /* ClassNames */

    const playersListClasses = cn(styles.playersList, {
        [styles.noPlayers]: game.players_joined <= 0,
    });

    return (
        <section className={cn(styles.stats, className)}>
            {game.players_joined > 1 && (
                <>
                    {openingTrans(
                        (style, item) =>
                            item && (
                                <animated.div
                                    style={style}
                                    className={styles.stats__playersContainer}
                                >
                                    <div className={playersListClasses}>
                                        {PlayersList}
                                    </div>
                                </animated.div>
                            ),
                    )}
                </>
            )}
            <div>
                <>{!closeStats && PlayersDeposits}</>
            </div>
        </section>
    );
};
export default memo(JackpotStats);
